<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapState, mapWritableState } from 'pinia';
import { usePoolsStore } from '@/stores/pools';
import Decimal from 'decimal.js-light';
import TableFiller from '@/components/table-filler.vue';
import PoolsAdd from '@/components/pools/pools/pools-add.vue';
import { catchError } from '@/helpers/toast';
import { updatePool, deletePool } from '@/api/admin-be';
import type { UpdatePool } from '@/types/pools';
import { formatDate } from '@/utils/format';

export default defineComponent({
  name: 'pools-list',
  components: { PoolsAdd, TableFiller },
  computed: {
    ...mapState(usePoolsStore, ['pools', 'poolsCount', 'filterPoolId']),
    ...mapWritableState(usePoolsStore, ['currentPoolId', 'poolsOffset', 'poolsSize']),
    poolsPrecalculated() {
      if (!this.pools) return {};

      const precalculated: {
        [key: number]: {
          jackpotAmount: Decimal;
          jackpotCarryover: Decimal;
          stakesAmount: Decimal;
          winsAmount: Decimal;
        };
      } = {};

      for (const pool of this.pools) {
        const jackpotAmount = this.getPoolJackpotAmount(pool);
        const jackpotWins = this.getPoolJackpotWins(pool);
        precalculated[pool.id] = {
          jackpotAmount: jackpotAmount,
          jackpotCarryover: jackpotAmount.plus(jackpotWins),
          stakesAmount: this.getPoolStakesAmount(pool),
          winsAmount: this.getPoolWinsAmount(pool),
        };
      }

      return precalculated;
    },
  },
  data() {
    return {
      statuses: {
        null: 'Черновик',
        Scheduled: 'Приём ставок',
        InProgress: 'Разыгрывается',
        Completed: 'Завершён',
        Cancelled: 'Отменён',
      },
      statusColors: {
        null: 'text-gray-500',
        Scheduled: 'text-blue-500',
        InProgress: 'text-yellow-500',
        Completed: 'text-green-500',
        Cancelled: 'text-red-500',
      },
      nullStatus: 'Черновик',
    };
  },
  methods: {
    formatDate,
    ...mapActions(usePoolsStore, ['fetchPools']),
    getPoolJackpotAmount(pool) {
      const jackpotTxs = pool.poolTransactions.filter(({ type }) => type === 'Jackpot');

      return jackpotTxs.reduce((acc, tx) => acc.plus(tx.vendorAmount), new Decimal(0));
    },
    getPoolJackpotWins(pool) {
      const jackpotTxs = [];

      for (const bet of pool.poolBets) {
        for (const tx of bet.poolBetTransactions) {
          if (tx.type === 'Jackpot') jackpotTxs.push(tx);
        }
      }

      return jackpotTxs.reduce((acc, tx) => acc.plus(tx.vendorAmount), new Decimal(0));
    },
    getPoolStakesAmount(pool) {
      return pool.poolBets.reduce(
        (acc, bet) =>
          acc.plus(
            bet.poolBetTransactions
              .filter((tx) => tx.type === 'Stake' && tx.amount > 0)
              .reduce((acc, tx) => acc.plus(tx.vendorAmount), new Decimal(0))
          ),
        new Decimal(0)
      );
    },
    getPoolWinsAmount(pool) {
      return pool.poolBets.reduce(
        (acc, bet) =>
          acc.plus(
            bet.poolBetTransactions
              .filter((tx) => tx.type === 'Stake' && tx.amount <= 0)
              .reduce((acc, tx) => acc.minus(tx.vendorAmount), new Decimal(0))
          ),
        new Decimal(0)
      );
    },
    updatePool(poolId: number, data: UpdatePool) {
      return updatePool(poolId, data)
        .then(() => this.fetchPools())
        .catch((error) => catchError('Ошибка обновления пула', error));
    },
    startPool(poolId: number) {
      return this.updatePool(poolId, { status: 'Scheduled' });
    },
    stopPool(poolId: number) {
      return this.updatePool(poolId, { status: null });
    },
    deletePool(poolId: number) {
      return deletePool(poolId)
        .then(() => {
          this.fetchPools();
          if (poolId === this.currentPoolId) {
            this.currentPoolId = null;
          }
        })
        .catch((error) => catchError('Ошибка удаления пула', error));
    },
  },
  mounted() {
    if (!this.pools) {
      this.fetchPools();
    }
  },
  watch: {
    filterPoolId() {
      this.fetchPools();
    },
  },
});
</script>

<template>
  <table class="basic-table" v-if="pools">
    <thead>
      <tr>
        <th style="width: 80px" rowspan="2">ID</th>
        <th rowspan="2">Название</th>
        <th style="width: 80px" rowspan="2">Тираж</th>
        <th rowspan="2">Статус</th>
        <th colspan="2">Прием ставок</th>
        <th rowspan="2">Тип пула</th>
        <th rowspan="2">Ставок</th>
        <th colspan="2">Сумма, USD</th>
        <th colspan="2">Джекпот, USD</th>
        <th rowspan="2"><i class="pi pi-cog" /></th>
      </tr>
      <tr>
        <th>Начало</th>
        <th>Конец</th>
        <th>Ставок</th>
        <th>Выигрышей</th>
        <th>Сумма</th>
        <th>Неразыграно</th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="pool of pools"
        :class="{
          'bg-green-50 dark:bg-green-950': pool.id === currentPoolId,
        }"
        @click.prevent="currentPoolId = pool.id"
      >
        <td>{{ pool.id }}</td>
        <td class="cursor-pointer !underline !text-blue-500 !text-sm">
          {{ pool.name }}
        </td>
        <td>{{ pool.round }}</td>
        <td :class="{ 'text-nowrap': true, [statusColors[pool.status]]: true }">
          {{ statuses[pool.status] }}
        </td>
        <td>{{ formatDate(pool.startBetAt, 'DD.MM.YYYY HH:mm') }}</td>
        <td>{{ formatDate(pool.endBetAt, 'DD.MM.YYYY HH:mm') }}</td>
        <td>{{ pool.poolType.name }}</td>
        <td>{{ pool.poolBets.length }}</td>
        <td>{{ poolsPrecalculated[pool.id].stakesAmount.toFixed(2) }}</td>
        <td>{{ poolsPrecalculated[pool.id].winsAmount.toFixed(2) }}</td>
        <td>{{ poolsPrecalculated[pool.id].jackpotAmount.toFixed(2) }}</td>
        <td>{{ poolsPrecalculated[pool.id].jackpotCarryover.toFixed(2) }}</td>
        <td class="!text-[15px]">
          <template v-if="pool.status === null">
            <i
              class="pi pi-play-circle cursor-pointer text-green-500 pt-1"
              @click.stop="startPool(pool.id)"
            />
            <i
              class="pi pi-trash text-red-500 text- cursor-pointer ml-2"
              @click.stop="deletePool(pool.id)"
            />
          </template>
          <template v-else-if="['Scheduled', 'InProgress'].includes(pool.status)">
            <i class="pi pi-stop-circle cursor-pointer text-red-500" @click.stop="stopPool(pool.id)" />
          </template>
        </td>
      </tr>
      <table-filler :max="poolsSize" :rows="pools" @click="() => currentPoolId = null"/>
      <slot />
    </tbody>
  </table>
</template>

<style scoped lang="scss"></style>
